// src/app/time-attendance/components/violations/violations.component.ts

import {
  Component,
  OnInit,
  OnDestroy,
  ChangeDetectorRef,
  ViewEncapsulation,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';

import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import customParseFormat from 'dayjs/plugin/customParseFormat';

import { ViolationsService } from './violations.service';
import {
  TimeViolation,
  TimeViolationsResponse,
  ViolationFilterOption,
  ViolationFilterValues,
} from '../../models/violations.model';
import { PunchesNavComponent } from '../../../shared/punches-nav/punches-nav.component';

dayjs.extend(utc);
dayjs.extend(customParseFormat);

interface ViolationFiltersVm {
  searchTerm: string;
  companyId: number | null;
  departmentId: number | null;
}

@Component({
  selector: 'app-violations',
  standalone: true,
  imports: [CommonModule, FormsModule, PunchesNavComponent],
  templateUrl: './violations.component.html',
  styleUrls: ['./violations.component.scss'],
  encapsulation: ViewEncapsulation.None,
})
export class ViolationsComponent implements OnInit, OnDestroy {
  violations: TimeViolation[] = [];

  isLoading = false;
  errorMessage: string | null = null;

  companiesIsLoading = false;
  departmentsIsLoading = false;
  companiesError: string | null = null;
  departmentsError: string | null = null;

  currentPage = 1;
  pageSize = 25;
  pageSizeOptions = [25, 50, 100, 500];
  totalPages = 1;

  companies: ViolationFilterOption[] = [];
  departments: ViolationFilterOption[] = [];

  expandedViolationIds = new Set<number>();
  togglingViolationIds = new Set<number>();

  filters: ViolationFiltersVm = {
    searchTerm: '',
    companyId: null,
    departmentId: null,
  };

  toastMessage = '';
  toastKind: 'success' | 'error' | 'info' = 'success';
  private toastTimeoutId: ReturnType<typeof setTimeout> | null = null;

  confirmModalOpen = false;
  violationToConfirm: TimeViolation | null = null;

  private readonly searchInput$ = new Subject<string>();

  constructor(
    private readonly violationsService: ViolationsService,
    private readonly cd: ChangeDetectorRef,
  ) {}

  ngOnInit(): void {
    this.bindSearch();
    this.loadFilterValues();
    this.loadViolations();
  }

  ngOnDestroy(): void {
    this.searchInput$.complete();

    if (this.toastTimeoutId) {
      clearTimeout(this.toastTimeoutId);
      this.toastTimeoutId = null;
    }
  }

  get hasActiveFilters(): boolean {
    return !!(
      this.filters.searchTerm.trim() ||
      this.filters.companyId != null ||
      this.filters.departmentId != null
    );
  }

  private bindSearch(): void {
    this.searchInput$
      .pipe(debounceTime(1000), distinctUntilChanged())
      .subscribe((value) => {
        this.filters.searchTerm = value;
        this.currentPage = 1;
        this.loadViolations();
      });
  }

  loadFilterValues(): void {
    this.companiesIsLoading = true;
    this.departmentsIsLoading = true;
    this.companiesError = null;
    this.departmentsError = null;

    this.violationsService.getFilterValues().subscribe({
      next: (res: ViolationFilterValues) => {
        this.companies = res.companies ?? [];
        this.departments = res.departments ?? [];
        this.companiesIsLoading = false;
        this.departmentsIsLoading = false;
        this.cd.detectChanges();
      },
      error: () => {
        this.companies = [];
        this.departments = [];
        this.companiesIsLoading = false;
        this.departmentsIsLoading = false;
        this.companiesError = 'Failed to load companies';
        this.departmentsError = 'Failed to load departments';
        this.cd.detectChanges();
      },
    });
  }

  loadViolations(): void {
    this.isLoading = true;
    this.errorMessage = null;
    this.cd.detectChanges();

    this.violationsService
      .getViolations(this.currentPage, this.pageSize, 'logTimeDesc', {
        searchTerm: this.filters.searchTerm,
        companyId: this.filters.companyId,
        departmentId: this.filters.departmentId,
      })
      .subscribe({
        next: (res: TimeViolationsResponse | null) => {
          if (!res) {
            this.violations = [];
            this.totalPages = 1;
            this.errorMessage = 'Failed to load violations';
            return;
          }

          this.violations = res.data ?? [];
          this.totalPages = res.totalPages ?? 1;

          const validIds = new Set(this.violations.map((violation) => violation.id));
          this.expandedViolationIds.forEach((id) => {
            if (!validIds.has(id)) {
              this.expandedViolationIds.delete(id);
            }
          });
        },
        error: () => {
          this.violations = [];
          this.totalPages = 1;
          this.errorMessage = 'Failed to load violations';
        },
        complete: () => {
          this.isLoading = false;
          this.cd.detectChanges();
        },
      });
  }

  onSearchTermChange(value: string | null): void {
    const normalized = String(value ?? '').trimStart();
    this.searchInput$.next(normalized);
  }

  onCompanyChange(value: number | null): void {
    this.filters.companyId = value;
    this.currentPage = 1;
    this.loadViolations();
  }

  onDepartmentChange(value: number | null): void {
    this.filters.departmentId = value;
    this.currentPage = 1;
    this.loadViolations();
  }

  clearFilters(): void {
    this.filters = {
      searchTerm: '',
      companyId: null,
      departmentId: null,
    };

    this.currentPage = 1;
    this.loadViolations();
  }

  nextPage(): void {
    if (this.currentPage < this.totalPages) {
      this.currentPage++;
      this.loadViolations();
    }
  }

  prevPage(): void {
    if (this.currentPage > 1) {
      this.currentPage--;
      this.loadViolations();
    }
  }

  changePageSize(): void {
    this.currentPage = 1;
    this.loadViolations();
  }

  toggleExpanded(violationId: number): void {
    if (this.expandedViolationIds.has(violationId)) {
      this.expandedViolationIds.delete(violationId);
      return;
    }

    this.expandedViolationIds.add(violationId);
  }

  isExpanded(violationId: number): boolean {
    return this.expandedViolationIds.has(violationId);
  }

  isTogglingApproval(violationId: number): boolean {
    return this.togglingViolationIds.has(violationId);
  }

  openConfirmModal(violation: TimeViolation): void {
    if (this.isTogglingApproval(violation.id)) {
      return;
    }

    this.violationToConfirm = violation;
    this.confirmModalOpen = true;
  }

  closeConfirmModal(): void {
    this.confirmModalOpen = false;
    this.violationToConfirm = null;
  }

  confirmToggleApproval(): void {
    if (!this.violationToConfirm) {
      return;
    }

    const violation = this.violationToConfirm;

    if (this.togglingViolationIds.has(violation.id)) {
      return;
    }

    this.togglingViolationIds.add(violation.id);
    this.closeConfirmModal();
    this.cd.detectChanges();

    const wasApproved = this.isApproved(violation);

    this.violationsService.toggleViolationApproval(violation.id).subscribe({
      next: (success) => {
        this.togglingViolationIds.delete(violation.id);

        if (!success) {
          this.showToast('Failed to update violation approval', 'error');
          this.cd.detectChanges();
          return;
        }

        this.showToast(
          wasApproved ? 'Violation rejected successfully' : 'Violation approved successfully',
          'success',
        );

        this.loadViolations();
      },
      error: () => {
        this.togglingViolationIds.delete(violation.id);
        this.showToast('Failed to update violation approval', 'error');
        this.cd.detectChanges();
      },
    });
  }

  trackByViolationId(_: number, item: TimeViolation): number {
    return item.id;
  }

  getEmployeeFullName(violation: TimeViolation): string {
    if (!violation.employee) {
      return '—';
    }

    const fullName =
      `${violation.employee.firstName ?? ''} ${violation.employee.lastName ?? ''}`.trim();

    return fullName || '—';
  }

  getEmployeeNumber(violation: TimeViolation): string {
    return violation.employee?.employeeNumber?.trim() || '—';
  }

  getEmployeeRfid(violation: TimeViolation): string {
    return violation.employee?.rfid?.trim() || '—';
  }

  getManagerName(violation: TimeViolation): string {
    const manager = violation.employee?.reportTo;

    if (!manager) {
      return '—';
    }

    const fullName = `${manager.firstName ?? ''} ${manager.lastName ?? ''}`.trim();
    return fullName || '—';
  }

  getViolationType(violation: TimeViolation): string {
    return violation.violation?.type?.trim() || '—';
  }

  getVerifiedByName(violation: TimeViolation): string {
    const verifiedBy = violation.violation?.verifiedBy;

    if (!verifiedBy) {
      return '—';
    }

    const fullName = `${verifiedBy.firstName ?? ''} ${verifiedBy.lastName ?? ''}`.trim();
    return fullName || '—';
  }

  isApproved(violation: TimeViolation): boolean {
    return !!violation.violation?.isApproved;
  }

  isViolation(violation: TimeViolation): boolean {
    return !this.isApproved(violation);
  }

  getApprovalButtonText(violation: TimeViolation): string {
    return this.isApproved(violation) ? 'Reject' : 'Approve';
  }

  getApprovalStatusLabel(violation: TimeViolation): string {
    return this.isApproved(violation) ? 'Approved' : 'Violation';
  }

  getConfirmModalTitle(): string {
    if (!this.violationToConfirm) {
      return 'Confirm Action';
    }

    return this.isApproved(this.violationToConfirm) ? 'Reject Violation' : 'Approve Violation';
  }

  getConfirmModalMessage(): string {
    if (!this.violationToConfirm) {
      return '';
    }

    const employeeName = this.getEmployeeFullName(this.violationToConfirm);
    const violationType = this.getViolationType(this.violationToConfirm);

    return this.isApproved(this.violationToConfirm)
      ? `Are you sure you want to reject ${violationType} for ${employeeName}?`
      : `Are you sure you want to approve ${violationType} for ${employeeName}?`;
  }

  getConfirmButtonText(): string {
    if (!this.violationToConfirm) {
      return 'Confirm';
    }

    return this.isApproved(this.violationToConfirm) ? 'Reject' : 'Approve';
  }

  getLogTypeLabel(violation: TimeViolation): string {
    if (violation.logType === 'ClockIn') {
      return 'In';
    }

    if (violation.logType === 'ClockOut') {
      return 'Out';
    }

    return violation.logType || '—';
  }

  getScheduleText(violation: TimeViolation): string {
    const start = violation.schedule?.localStartTime?.trim();
    const end = violation.schedule?.localEndTime?.trim();

    if (!start || !end) {
      return '—';
    }

    return `${this.formatLocalScheduleTime(start)} - ${this.formatLocalScheduleTime(end)}`;
  }

  formatLogDateTime(value: string | null | undefined): string {
    const parsed = this.parseUtcToLocal(value);
    return parsed ? parsed.format('MMM D, YYYY h:mm A') : '—';
  }

  formatVerifiedDateTime(value: string | null | undefined): string {
    return this.formatLogDateTime(value);
  }

  showToast(message: string, kind: 'success' | 'error' | 'info'): void {
    if (this.toastTimeoutId) {
      clearTimeout(this.toastTimeoutId);
      this.toastTimeoutId = null;
    }

    this.toastMessage = message;
    this.toastKind = kind;
    this.cd.detectChanges();

    this.toastTimeoutId = setTimeout(() => {
      this.clearToast();
    }, 2500);
  }

  clearToast(): void {
    if (this.toastTimeoutId) {
      clearTimeout(this.toastTimeoutId);
      this.toastTimeoutId = null;
    }

    this.toastMessage = '';
    this.cd.detectChanges();
  }

  private parseUtcToLocal(value: string | null | undefined): dayjs.Dayjs | null {
    if (!value?.trim()) {
      return null;
    }

    const parsed = dayjs.utc(value.trim());

    if (!parsed.isValid()) {
      return null;
    }

    return parsed.local();
  }

  private formatLocalScheduleTime(value: string): string {
    const parsed = dayjs(value, 'HH:mm:ss', true);

    if (!parsed.isValid()) {
      return value;
    }

    return parsed.format('h:mm A');
  }
}